import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Checkbox, Link, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import useUsers from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUsers';
import useManageUserGroupUser from 'views/App/Portal/LexID/Groups/_data_sources/useManageGroupUser';
import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import ListSection from 'components/_layouts/ListSection';
import AddUserReview from './AddUserReview';

const columnHelper = createColumnHelper<User>();

type AddUserProps = {
  searchQuery: string;
  step: 'select' | 'review';
  onSuccess?: () => void;
  onSelectionChange?: (count: number) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  submitRef?: React.MutableRefObject<(() => void) | null>;
  existingUserIds?: string[];
};

export default function AddUser({ searchQuery, step, onSuccess, onSelectionChange, onSubmittingChange, submitRef, existingUserIds = [] }: AddUserProps) {
  const { currentOrganizationId } = useContext(AppContext);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const userOptions = useMemo(
    () => ({ status: 'ACTIVE', ...(searchQuery && { search: searchQuery }) }),
    [searchQuery]
  );

  const usersQuery = useUsers(currentOrganizationId || '', userOptions);
  const { addUsersBulk } = useManageUserGroupUser();

  const visibleUsers = useMemo(
    () => (usersQuery.data?.pages ? usersQuery.data.pages.flatMap((x) => x.content) : []),
    [usersQuery.data]
  );

  const filteredVisibleUsers = useMemo(
    () => visibleUsers.filter((u) => !existingUserIds.includes(u.id || '')),
    [visibleUsers, existingUserIds]
  );

  const selectedIds = useMemo(() => new Set(selectedUsers.map((u) => u.id)), [selectedUsers]);

  const allVisibleSelected =
    filteredVisibleUsers.length > 0 && filteredVisibleUsers.every((u) => selectedIds.has(u.id));
  const someVisibleSelected =
    filteredVisibleUsers.some((u) => selectedIds.has(u.id)) && !allVisibleSelected;

  const handleToggleUser = (user: User) => {
    setSelectedUsers((prev) => {
      if (prev.some((u) => u.id === user.id)) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const handleToggleAll = () => {
    if (allVisibleSelected) {
      setSelectedUsers((prev) => prev.filter((u) => !filteredVisibleUsers.some((v) => v.id === u.id)));
    } else {
      setSelectedUsers((prev) => {
        const prevIds = new Set(prev.map((u) => u.id));
        const toAdd = filteredVisibleUsers.filter((u) => !prevIds.has(u.id));
        return [...prev, ...toAdd];
      });
    }
  };

  useEffect(() => {
    onSelectionChange?.(selectedUsers.length);
  }, [selectedUsers, onSelectionChange]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleBulkAdd;
    }
  });

  const handleBulkAdd = async () => {
    if (!currentOrganizationId || !id || selectedUsers.length === 0) return;
    setIsSubmitting(true);
    onSubmittingChange?.(true);
    const userIds = selectedUsers.map((u) => u.id).filter((uid): uid is string => Boolean(uid));
    try {
      await addUsersBulk(currentOrganizationId, id, userIds, () => {
        queryClient.invalidateQueries(['group_users', currentOrganizationId, id]);
        setSelectedUsers([]);
        enqueueSnackbar(
          t('groups.details.addUsersSuccess', 'Adding the users, you will receive a notification when the process is complete.'),
          { variant: 'success' }
        );
        if (onSuccess) onSuccess();
      });
    } catch {
      enqueueSnackbar(t('groups.details.addUsersFailed', 'Failed to add users to the group. Please try again.'), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'select',
      cell: (info) => (
        <Checkbox
          checked={selectedIds.has(info.row.original.id)}
          onChange={() => handleToggleUser(info.row.original)}
          size='small'
        />
      ),
      header: () => (
        <Checkbox
          checked={allVisibleSelected}
          indeterminate={someVisibleSelected}
          onChange={handleToggleAll}
          size='small'
        />
      ),
      size: 52,
    }),
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: 'name',
      cell: (info) => (
        <Link
          href={`/admin/lexid/users/${info.row.original.id}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Typography sx={{ fontWeight: 'bold' }}>
            {`${info.row.original.firstName} ${info.row.original.lastName}`}
          </Typography>
        </Link>
      ),
      header: () => t('common.name', 'Name'),
      size: 9999,
    }),
    columnHelper.accessor('email', {
      cell: (info) => info.getValue() ?? '',
      header: () => t('common.email', 'Email'),
      size: 9999,
    }),
    columnHelper.accessor('active', {
      cell: (info) => (
        <Badge
          variant='inline'
          badgeContent={info.getValue() ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
          color={info.getValue() ? 'success' : 'error'}
          sx={{ textTransform: 'uppercase' }}
        />
      ),
      header: () => t('common.active', 'Active'),
      size: 120,
    }),
  ];

  if (step === 'review') {
    return (
      <AddUserReview
        selectedUsers={selectedUsers}
        onRemoveUser={(userId) => setSelectedUsers((prev) => prev.filter((u) => u.id !== userId))}
        searchQuery={searchQuery}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <ListSection
        columns={columns}
        data={filteredVisibleUsers}
        query={usersQuery}
        isFiltered={Boolean(searchQuery)}
        toolbarLeft={undefined}
        toolbarRight={undefined}
      />
    </Stack>
  );
}
